import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import React, {useEffect, useMemo, useRef} from 'react';

import {ContextMenuOption} from 'components/ContextMenu';
import {
  EMPTY_SEARCH_RESULT_PROPS,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import SimpleTableEditable from 'notebook/simple-tables/SimpleTableEditable';
import useHydratedStructuredTableData from 'notebook/structured-tables/hooks/useHydratedStructuredTableData';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {OutsideNotebookContext} from 'notebook/structured-tables/StructuredTableOutsideNotebookConstants';
import getActionFunctions from 'notebook/structured-tables/util/getActionFunctions';
import getAddFromSourceButton from 'notebook/structured-tables/util/getAddFromSourceButton';
import StructuredTableContext from 'notebook/structured-tables/util/StructuredTableContext';
import useStructuredTableContexts from 'notebook/structured-tables/util/useStructuredTableContexts';
import type {ColumnConstraint} from 'notebook/tables/constants/TableColumnConstants';
import * as TableConstants from 'notebook/tables/constants/TableConstants';
import type * as TableLayoutConstants from 'notebook/tables/constants/TableLayoutConstants';
import {SelectionRange} from 'notebook/tables/constants/TableSelectionConstants';
import MediocreWrapper from 'notebook/tables/core-ui/MediocreWrapper';
import {EvaluatedCell, EvaluatedCellError} from 'notebook/tables/excess/EvaluationConstants';
import {EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import {TableOpDiff} from 'notebook/tables/excess/ops/TableOpConstants';
import {
  insertRowsHelper,
  ReorderOptions,
  reorderRowsOrColumns,
  wrapTableOpForSingleDoc,
} from 'notebook/tables/excess/ops/TableOps';
import {hasAnyErrors, hasAnyWarnings} from 'notebook/tables/excess/Util';
import {evaluateTables} from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import {useTableViewsContext} from 'notebook/tables/table-views/TableViewsContext';
import {getStore} from 'redux-stores/Store';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';

import {useActionFunctionContext} from './action-functions/ActionFunctionContext';

export function buildComputedTable<T extends UnknownStructuredTable>(
  structuredTable: T,
  oldComputedTable: EvaluatedTable | null
): EvaluatedTable {
  const adapter = getStructuredTableAdapters()[structuredTable.kind];
  const {computedTables} = evaluateTables({
    fullTableMap: {[structuredTable.cid]: adapter.toTable(structuredTable)},
    oldComputedTables: oldComputedTable != null ? {[structuredTable.cid]: oldComputedTable} : null,
    hasFormulasDisabled: false,
    selectors: [],
    spreadsheetEvalCacheKey: null,
  });
  return computedTables[structuredTable.cid];
}

function useComputedTable<T extends UnknownStructuredTable>(structuredTable: T): EvaluatedTable {
  const tableRef = useRef<EvaluatedTable | null>(null);
  const computedTable = useMemo(
    () => buildComputedTable(structuredTable, tableRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [structuredTable, getStore().getState().models.eln.resolutionResultsByResolutionQuery]
  );
  tableRef.current = computedTable;
  return computedTable;
}

export type StructuredTableUpdateHandler = (
  structuredTable: UnknownStructuredTable,
  overrideArchivePurposeByRow?: Array<string | null | undefined>
) => void;

export interface StructuredTableOutsideNotebookProps {
  structuredTable: UnknownStructuredTable;
  cid: string;
  locked?: boolean;
  isProtected?: boolean;
  // By default, action functions are accessible even if the table cells are locked. Pass this in to also
  // lock the action functions
  lockActionFunctions?: boolean;
  updateHandler: StructuredTableUpdateHandler;
  onComputedTableChanged?: (computedTable: Array<Array<EvaluatedCell>>) => void;
  onErrorRowsChanged?: (errorRows: Array<Array<EvaluatedCellError>>) => void;
  /**
   * If true, renders column resize handles. Defaults to false because STON does not persist widths on
   * its own. For widths that stay adjustable regardless of persistence, wrap with
   * `useStructuredTableColumnWidths`.
   */
  enableColumnResizing?: boolean;
  canAddColumnsFreeform?: boolean;
  disableMaximizedTables?: boolean;
  isMaximized?: boolean;
  isSplitScreen?: boolean;
  isFocused?: boolean;
  setMaximizedTable?: (focusedTableProvenanceUuid: string | null) => void;
  setSplitScreenTable?: (focusedTableProvenanceUuid: string | null) => void;
  hasPrimarySamples?: boolean;
  includeUnsubmittedStatus?: boolean;
  hideLeftHeaderControls?: boolean;
  outsideNotebookContext?: OutsideNotebookContext | null;
  hideColumnHeaderIcons?: boolean;
  hideFooter?: boolean;
  isCollapsible?: boolean;
  iconOverride?: string;
  canRename?: boolean;
  suppressTitleBar?: boolean;
  enableFormulas?: boolean;
  preserveMergedCellsInPaste?: boolean;
  dataTestAttrs?: DataTestAttrs;
  maximumHeight?: TableLayoutConstants.MaximumTableHeightType;
  getExtraColumnContextMenuOptionsForStructuredTable?: (
    selectionRange: SelectionRange | null,
    table: TableConstants.Table,
    updateColumnConstraint?: (index: number, columnConstraint: ColumnConstraint) => void
  ) => Array<ContextMenuOption> | null;
  getExtraRowAxisContextMenuOptions?: (
    selectionRange: SelectionRange | null
  ) => Array<ContextMenuOption> | null;
  searchResultProps?: MediocreSearchResultProps;
}

export function StructuredTableOutsideNotebookWithTableViews(
  props: StructuredTableOutsideNotebookProps
): JSX.Element {
  const {focusedStructuredTableCid, focusedTableViewMode, provideStructuredTable, setTableView} =
    useTableViewsContext();

  useEffect(() => {
    // Only provide the structured table if it is not a Table View
    if ((!props.isMaximized && !props.isSplitScreen) || props.isFocused) {
      provideStructuredTable(props);
    }
  }, [props, provideStructuredTable]);

  return (
    <div
      id={props.cid}
      {...getTestProps({key: 'StructuredTableOutsideNotebookWithTableViews'})}
      data-amp-safe={false}
    >
      <StructuredTableOutsideNotebook
        {...props}
        disableMaximizedTables={false}
        isFocused={props.isFocused ?? focusedStructuredTableCid === props.cid}
        isMaximized={
          props.isMaximized ??
          (focusedStructuredTableCid === props.cid &&
            focusedTableViewMode === TableConstants.FocusedTableViewMode.FULLSCREEN)
        }
        isSplitScreen={
          props.isSplitScreen ??
          (focusedStructuredTableCid === props.cid &&
            focusedTableViewMode === TableConstants.FocusedTableViewMode.HORIZONTAL_SPLIT)
        }
        setMaximizedTable={(focusedTableProvenanceUuid: string | null) => {
          // Use the cid to track table view state, since provenanceUuid is generated and not visible outside
          // the notebook
          setTableView(
            focusedTableProvenanceUuid ? props.cid : null,
            TableConstants.FocusedTableViewMode.FULLSCREEN
          );
        }}
        setSplitScreenTable={(focusedTableProvenanceUuid: string | null) => {
          // Use the cid to track table view state, since provenanceUuid is generated and not visible outside
          // the notebook
          setTableView(
            focusedTableProvenanceUuid ? props.cid : null,
            TableConstants.FocusedTableViewMode.HORIZONTAL_SPLIT
          );
        }}
      />
    </div>
  );
}

export default function StructuredTableOutsideNotebook(
  props: StructuredTableOutsideNotebookProps
): JSX.Element {
  const {
    structuredTable,
    cid,
    updateHandler,
    onComputedTableChanged,
    onErrorRowsChanged,
    locked = false,
    hasPrimarySamples = true,
    includeUnsubmittedStatus = true,
    hideLeftHeaderControls = false,
    hideColumnHeaderIcons = false,
    isCollapsible = true,
    enableFormulas = false,
  } = props;
  const adapter = getStructuredTableAdapters()[structuredTable.kind];
  // TODO(BNCH-34087): refactor computed table evaluation such that this isn't necessary
  const computedTable = useComputedTable(structuredTable);
  const localUpdateHandler = (index: number, {table}: {table: TableConstants.Table}): void => {
    updateHandler(adapter.fromTable(table));
  };
  const {
    table: originalTable,
    liveRowMetadataByRow,
    tableStatus,
    errorRows,
  } = useHydratedStructuredTableData({
    adapter,
    structuredTable,
    computedTable,
    validationErrors: [],
    options: {hasPrimarySamples, includeUnsubmittedStatus},
  });

  useEffect(() => {
    onErrorRowsChanged?.(errorRows);
  }, [errorRows, onErrorRowsChanged]);

  const actionFunctionContext = useActionFunctionContext();
  const {rightHeaderControls, contextMenuOptionGetters} = getActionFunctions({
    entryId: null,
    structuredTable,
    adapter,
    locked: props.lockActionFunctions ?? false,
    computedTable,
    liveRowMetadataByRow,
    tableStatus,
    outsideNotebookContext: props.outsideNotebookContext,
    updateHandler: ({
      updatedCells,
      columnUpdates,
      lastSyncedUpstreamValuesByRow,
      lastSyncedArchivePurposeByRow,
      isFinalizedByRow,
    }) => {
      const newTable = adapter.fromTable({...adapter.toTable(structuredTable), table: updatedCells});
      newTable.rows = newTable.rows.map((row, idx) => ({
        ...row,
        ...(isFinalizedByRow != null ? {isFinalized: isFinalizedByRow[idx]} : {}),
        ...(lastSyncedUpstreamValuesByRow != null
          ? {lastSyncedUpstreamValues: lastSyncedUpstreamValuesByRow[idx]}
          : {}),
      }));

      updateHandler(newTable, lastSyncedArchivePurposeByRow);
    },
    performTableOp: () => {},
    actionFunctionContext,
    isFilterStale: false,
  });
  const table = locked
    ? {
        ...originalTable,
        table: originalTable.table.map((row) => row.map((cell) => ({...cell, locked: true}))),
      }
    : originalTable;
  const structuredTableContexts = useStructuredTableContexts();
  const leftHeaderControls = ({isCollapsed}: TableConstants.TableHeaderControlsArgs): JSX.Element | null => {
    if (isCollapsed || hideLeftHeaderControls) {
      return null;
    }

    // TODO(BNCH-136540): Update STONs to support table edit ops.
    const afstUpdateHandler = (table: UnknownStructuredTable, tableOp?: TableOpDiff): void => {
      props.updateHandler(table);
    };

    return getAddFromSourceButton({
      addOperations: adapter.getAddFromSourceOperations('', props.structuredTable),
      adapter,
      structuredTable: props.structuredTable,
      table,
      tableIsLocked: Boolean(props.locked),
      updateHandler: afstUpdateHandler,
      contexts: structuredTableContexts,
      insertAfterTable: noop,
    });
  };

  const insertRowsAboveHandler = (index: number, start: number, end: number): void => {
    const tableName = 'tableOutsideNotebook';
    const tableMap = {[tableName]: table};
    const newTable = wrapTableOpForSingleDoc((table, documentName) =>
      insertRowsHelper(table, documentName, start, end - start + 1)
    )(tableMap, {}, tableName).tables[tableName];
    updateHandler(adapter.fromTable(newTable));
  };

  const insertRowsBelowHandler = (index: number, start: number, end: number): void => {
    const tableName = 'tableOutsideNotebook';
    const tableMap = {[tableName]: table};
    const newTable = wrapTableOpForSingleDoc((table, documentName) =>
      insertRowsHelper(table, documentName, end + 1, end - start + 1)
    )(tableMap, {}, tableName).tables[tableName];
    updateHandler(adapter.fromTable(newTable));
  };

  const reorderRowsOrColumnsHandler = (index: number, options: ReorderOptions): void => {
    const tableName = 'tableOutsideNotebook';
    const tableMap = {[tableName]: table};
    const newTable = wrapTableOpForSingleDoc((table, documentName) =>
      reorderRowsOrColumns(table, documentName, options)
    )(tableMap, {}, tableName).tables[tableName];
    updateHandler(adapter.fromTable(newTable));
  };
  const getExtraContextMenuOptionsForStructuredTable =
    props.getExtraColumnContextMenuOptionsForStructuredTable;

  const getExtraRowAxisContextMenuOptions =
    props.getExtraRowAxisContextMenuOptions ?? contextMenuOptionGetters.rowAxis;

  const allowReorderDimensions = useMemo(
    () => adapter.allowReorderDimensions(structuredTable),
    [structuredTable, adapter]
  );

  return (
    <MediocreWrapper>
      <StructuredTableContext.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
        value={{
          structuredTableKind: structuredTable.kind,
          provenanceUuid: structuredTable.provenanceUuid,
        }}
      >
        <SimpleTableEditable
          allowReorderDimensions={allowReorderDimensions}
          canAddRemoveColumns={true}
          enableColumnResizing={Boolean(props.enableColumnResizing)}
          canAddColumnsFreeform={props.canAddColumnsFreeform}
          cid={cid}
          disableMaximizedTables={props.disableMaximizedTables}
          setMaximizedTable={props.setMaximizedTable}
          setSplitScreenTable={props.setSplitScreenTable}
          isMaximized={props.isMaximized}
          isSplitScreen={props.isSplitScreen}
          isFocused={props.isFocused}
          enableFillDown={true}
          enableFormulas={enableFormulas}
          dataTestAttrs={props.dataTestAttrs}
          deleteHandler={noop}
          fileId={null}
          forceCollapsed={false}
          hasPlaceholderRows={false}
          headerControls={rightHeaderControls}
          leftHeaderControls={leftHeaderControls}
          hideColumnHeaderIcons={hideColumnHeaderIcons}
          hideFormulaBox={true}
          hideFooter={props.hideFooter}
          icon={props.iconOverride ?? adapter.getTableIcon(structuredTable)}
          indentation={0}
          index={0}
          insertItemHandler={noop}
          insertRowsAbove={insertRowsAboveHandler}
          insertRowsBelow={insertRowsBelowHandler}
          reorderRowsOrColumns={reorderRowsOrColumnsHandler}
          isCollapsible={isCollapsible}
          isCompactBottomSpacing={false}
          isLeftAlignColumnHeader={true}
          isTableNameUsed={() => false}
          getExtraRowAxisContextMenuOptions={getExtraRowAxisContextMenuOptions}
          locked={locked}
          isProtected={props.isProtected}
          mouseDownHandler={noop}
          mouseOverHandler={noop}
          moveHandler={noop}
          onComputedTableChanged={onComputedTableChanged}
          requestSelection={noop}
          restoreState={noop}
          setClipboard={noop}
          setSelectionAbove={noop}
          setSelectionBelow={noop}
          storeState={noop}
          suppressTitleBar={props.suppressTitleBar}
          tabHandler={noop}
          table={table}
          tableLabel={adapter.getDisplayName(structuredTable)}
          updateHandler={localUpdateHandler}
          canRename={props.canRename}
          preserveMergedCellsInPaste={props.preserveMergedCellsInPaste}
          maximumHeight={props.maximumHeight}
          errorRows={errorRows.map((errors) => errors.some((error) => hasAnyErrors(error)))}
          warningRows={errorRows.map((errors) => errors.some((error) => hasAnyWarnings(error)))}
          getExtraContextMenuOptions={
            getExtraContextMenuOptionsForStructuredTable != null
              ? (selectionRange, updateColumnConstraint) =>
                  getExtraContextMenuOptionsForStructuredTable(selectionRange, table, updateColumnConstraint)
              : undefined
          }
          {...(props.searchResultProps ?? EMPTY_SEARCH_RESULT_PROPS)}
        />
      </StructuredTableContext.Provider>
    </MediocreWrapper>
  );
}
